<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { computed } from 'vue'
import PrimeInputNumber, { type InputNumberInputEvent } from 'primevue/inputnumber'
import { useFormFieldContext } from '@uiKit/composables/useFormFieldContext'

export type NumberInputMode = 'decimal' | 'currency'

interface Props {
  /** Bound value. `null` clears the input. */
  modelValue?: number | null
  /** Minimum allowed value (inclusive). */
  min?: number
  /** Maximum allowed value (inclusive). */
  max?: number
  /** Minimum fraction digits to render. */
  minFractionDigits?: number
  /** Maximum fraction digits to render. Falls back to `minFractionDigits` when omitted. */
  maxFractionDigits?: number
  /** Empty-state placeholder text. */
  placeholder?: string
  /** Disable user interaction (greyed out). */
  disabled?: boolean
  /** Render the value read-only — visible and selectable, but not editable. */
  readonly?: boolean
  /** Apply the invalid-state styling. */
  invalid?: boolean
  /** Extra class names applied to the inner `<input>` element. */
  inputClass?: string
  /** Show thousands separators (locale-aware grouping). */
  useGrouping?: boolean
  /** Display mode — `decimal` (default) or `currency`. */
  mode?: NumberInputMode
  /** ISO currency code (e.g. `'AED'`). Only relevant when `mode === 'currency'`. */
  currency?: string
  /** BCP 47 locale tag for grouping + currency formatting (e.g. `'en-US'`). */
  locale?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  minFractionDigits: 2,
  disabled: false,
  readonly: false,
  invalid: false,
  inputClass: '',
  useGrouping: true,
  mode: 'decimal',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null): void
  /** Fires on every keystroke with the parsed value — `update:modelValue` only fires on blur. */
  (e: 'input', value: number | null): void
}>()

function handleInput(event: InputNumberInputEvent) {
  emit('input', typeof event.value === 'number' ? event.value : null)
}

const inputClasses = computed(() => ['number-input-field', props.inputClass])

const field = useFormFieldContext()
</script>

<template>
  <PrimeInputNumber
    :inputId="field?.controlId"
    :ariaLabelledby="field?.labelId"
    :modelValue="props.modelValue"
    :mode="props.mode"
    :currency="props.currency"
    :locale="props.locale"
    :min="props.min"
    :max="props.max"
    :minFractionDigits="props.minFractionDigits"
    :maxFractionDigits="props.maxFractionDigits ?? props.minFractionDigits"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :invalid="props.invalid"
    :useGrouping="props.useGrouping"
    class="number-input"
    :inputClass="inputClasses"
    @update:modelValue="emit('update:modelValue', $event)"
    @input="handleInput"
  />
</template>

<style>
/* Unscoped on purpose -- targets PrimeVue inner / teleported DOM that scoped CSS cannot reach. */
.number-input {
  width: 100%;
}

.number-input-field {
  width: 100%;
  height: 42px;
  border-radius: 6px;
  border: 1px solid var(--p-surface-300);
  background: var(--p-surface-0);
  font-size: 0.9375rem;
  color: var(--p-text-color);
  padding: 0 0.75rem;
  font-family: inherit;
}

.number-input-field:not(:disabled):not([readonly]):hover {
  border-color: var(--p-primary-300);
}

/* Invalid wins over hover — the red border must not vanish under the cursor (BUG-024). */
.number-input.p-invalid .number-input-field,
.number-input-field.p-invalid,
.number-input.p-invalid .number-input-field:not(:disabled):not([readonly]):hover,
.number-input-field.p-invalid:not(:disabled):not([readonly]):hover {
  border-color: var(--p-danger-600);
}

.number-input-field:not([readonly]):focus {
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 1px var(--p-primary-400);
  outline: none;
}

/* Read-only mirrors the disabled look — greyed surface, muted text, no caret. */
.number-input-field[readonly] {
  background: var(--p-surface-100);
  color: var(--p-text-muted-color);
  border-color: var(--p-surface-200);
  cursor: not-allowed;
}
</style>
